import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import DescriptionIcon from '@mui/icons-material/Description';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { logger, fileLogger } from '../utils/logger';
import { getViewedUser, isInTeamManagement } from '../utils/viewedUserStore';
import { usePerformanceMonitor } from '../utils/performanceMonitor';
import { t } from '../utils/localization/localization';
import departmentService from '../services/departmentService';
import CVAnalyzerPage from './CVAnalyzerPage';
import JobAnalyzerPage from './JobAnalyzerPage';
import ResultPage from './ResultPage';
import ReportsView from '../components/reports/ReportsView';

function TeamManagementPage() {
  const { currentUser, userRole, getUserSubordinates, getAllUsers, ROLES } = useAuth();
  const navigate = useNavigate();

  const [activeTab] = useState(0);
  const [subordinates, setSubordinates] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [filteredSubordinates, setFilteredSubordinates] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [nameSearchFilter, setNameSearchFilter] = useState('');
  const [departments, setDepartments] = useState([]);

  // Performance monitoring
  usePerformanceMonitor('TeamManagementPage', {
    activeTab,
    subordinatesCount: subordinates.length,
    filteredSubordinatesCount: filteredSubordinates.length,
    selectedUser: selectedUser?.id,
    viewMode,
    currentUser: currentUser?.id,
    userRole,
    departmentFilter,
    nameSearchFilter
  });

  const handleVersionPersistence = useCallback(async (targetUserId) => {
    logger.info('TeamManagementPage', 'Version persistence placeholder', { targetUserId });
  }, []);

  // Load departments for filtering
  const loadDepartments = useCallback(async () => {
    try {
      logger.info('TeamManagementPage', 'Loading departments for filtering');
      const departmentOptions = await departmentService.getDepartmentOptions(true);
      setDepartments(departmentOptions);
      logger.info('TeamManagementPage', `Loaded ${departmentOptions.length} department options`);
    } catch (error) {
      logger.error('TeamManagementPage', 'Error loading departments', error);
      fileLogger.error('TEAM_MANAGEMENT', 'Error loading departments', { error: error.message });
      setDepartments([]);
    }
  }, []);

  // Smart search function for names (fuzzy search)
  const isNameMatch = useCallback((userName, searchTerm) => {
    if (!searchTerm) return true;

    const normalizedName = userName.toLowerCase();
    const normalizedSearch = searchTerm.toLowerCase();

    // Exact match
    if (normalizedName.includes(normalizedSearch)) return true;

    // Fuzzy match - allow for minor typos and word order
    const nameWords = normalizedName.split(' ');
    const searchWords = normalizedSearch.split(' ');

    // Check if all search words are found in name words (partial matches)
    return searchWords.every(searchWord =>
      nameWords.some(nameWord =>
        nameWord.includes(searchWord) || searchWord.includes(nameWord)
      )
    );
  }, []);

  // Apply filters to subordinates
  const applyFilters = useCallback(() => {
    let filtered = [...subordinates];

    // Apply department filter
    if (departmentFilter) {
      if (departmentFilter === 'null') {
        // Filter for users with no department
        filtered = filtered.filter(user => !user.department);
      } else {
        // Filter for specific department
        filtered = filtered.filter(user => user.department === departmentFilter);
      }
    }

    // Apply name search filter
    if (nameSearchFilter) {
      filtered = filtered.filter(user => isNameMatch(user.name, nameSearchFilter));
    }

    setFilteredSubordinates(filtered);

    logger.info('TeamManagementPage', 'Filters applied', {
      originalCount: subordinates.length,
      filteredCount: filtered.length,
      departmentFilter,
      nameSearchFilter
    });
  }, [subordinates, departmentFilter, nameSearchFilter, isNameMatch]);

  // Handle clear filters
  const handleClearFilters = useCallback(() => {
    setDepartmentFilter('');
    setNameSearchFilter('');
    logger.info('TeamManagementPage', 'Filters cleared');
    fileLogger.info('TEAM_MANAGEMENT', 'User cleared all filters');
  }, []);

  const loadSubordinates = useCallback(async () => {
    if (currentUser && (userRole === ROLES.MANAGER || userRole === ROLES.ADMIN)) {
      try {
        let userSubordinates;

        if (userRole === ROLES.ADMIN) {
          // Admins can see all employees
          const allUsers = await getAllUsers();
          // Filter out the current admin user from the list
          userSubordinates = allUsers.filter(user => user.id !== currentUser.id);
          logger.info('TeamManagementPage', 'Loaded all employees for admin', { employeesCount: userSubordinates.length });
        } else {
          // Managers only see their subordinates
          userSubordinates = await getUserSubordinates(currentUser.id);
          logger.info('TeamManagementPage', 'Loaded subordinates for manager', { subordinatesCount: userSubordinates.length });
        }

        setSubordinates(userSubordinates);
        setFilteredSubordinates(userSubordinates);

        const persistedUserId = getViewedUser();
        if (persistedUserId && isInTeamManagement()) {
          fileLogger.info('TEAM_MANAGEMENT', 'Restoring viewed user after page refresh', {
            persistedUserId,
            isTeamManagement: true
          });

          const persistedUser = userSubordinates.find(user => user.id === persistedUserId);
          if (persistedUser) {
            fileLogger.info('TEAM_MANAGEMENT', 'Found persisted user in subordinates', {
              persistedUser: persistedUser.name
            });
            setSelectedUser(persistedUser);
            setViewMode('job');
          } else {
            fileLogger.warn('TEAM_MANAGEMENT', 'Persisted user not found in subordinates', {
              persistedUserId
            });
          }
        }
      } catch (error) {
        logger.error('TeamManagementPage', 'Error loading subordinates', error);
        fileLogger.error('TEAM_MANAGEMENT', 'Error loading subordinates', { error: error.message });
      }
    } else if (userRole && userRole !== ROLES.MANAGER && userRole !== ROLES.ADMIN) {
      navigate('/');
    }
  }, [currentUser, userRole, getUserSubordinates, getAllUsers, ROLES.MANAGER, ROLES.ADMIN, navigate]);

  useEffect(() => {
    loadSubordinates();
    loadDepartments();
  }, [loadSubordinates, loadDepartments]);

  useEffect(() => {
    if (selectedUser) {
      handleVersionPersistence(selectedUser.id);
    }
  }, [selectedUser, handleVersionPersistence]);

  // Apply filters whenever subordinates or filter values change
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleCV = (user) => {
    fileLogger.info('TEAM_MANAGEMENT', 'Viewing CV for user', {
      userId: user.id,
      userName: user.name
    });
    setSelectedUser(user);
    setViewMode('cv');
  };

  const handleJobSkills = (user) => {
    fileLogger.info('TEAM_MANAGEMENT', 'Viewing job skills for user', {
      userId: user.id,
      userName: user.name
    });
    setSelectedUser(user);
    setViewMode('job');
  };

  const handleTrainingRecommendations = (user) => {
    fileLogger.info('TEAM_MANAGEMENT', 'Viewing training recommendations for user', {
      userId: user.id,
      userName: user.name,
      currentUserId: currentUser?.id,
      currentUserRole: userRole
    });
    logger.info('TRAINING_RECOMMENDATIONS_CLICK', 'Training recommendations button clicked', {
      targetUser: {
        id: user.id,
        name: user.name,
        role: user.role,
        manager_id: user.manager_id
      },
      currentUser: {
        id: currentUser?.id,
        role: userRole
      }
    });
    setSelectedUser(user);
    setViewMode('training');
  };

  const handleReports = () => {
    fileLogger.info('TEAM_MANAGEMENT', 'Viewing skills gap reports', {
      currentUserId: currentUser?.id,
      currentUserRole: userRole
    });
    logger.info('REPORTS_VIEW', 'Skills gap reports button clicked', {
      currentUser: {
        id: currentUser?.id,
        role: userRole
      }
    });
    setSelectedUser(null);
    setViewMode('reports');
  };

  const handleBackToList = () => {
    fileLogger.info('TEAM_MANAGEMENT', 'Returning to team list view');
    setSelectedUser(null);
    setViewMode('list');
  };

  const renderContent = () => {
    if (activeTab !== 0) {
      return (
        <Typography variant="body1" paragraph align="center">
          {t('teamManagement.featureComingSoon')}
        </Typography>
      );
    }

    if (viewMode === 'list') {
      return (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">
              {userRole === ROLES.ADMIN ? t('teamManagement.allEmployees') : t('teamManagement.yourTeamMembers')}
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AssessmentIcon />}
              onClick={handleReports}
              sx={{
                textTransform: 'none'
              }}
            >
              {t('teamManagement.skillsGapReport')}
            </Button>
          </Box>

          {/* Filter Controls */}
          {subordinates.length > 0 && (
            <Paper
              elevation={1}
              sx={{ p: 2, mb: 3, backgroundColor: 'background.default' }}
            >
              <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <FilterListIcon sx={{ mr: 1 }} />
                {t('common.filters')}
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label={t('teamManagement.searchByName')}
                    value={nameSearchFilter}
                    onChange={(e) => setNameSearchFilter(e.target.value)}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                    placeholder={t('teamManagement.searchPlaceholder')}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('teamManagement.filterByDepartment')}</InputLabel>
                    <Select
                      value={departmentFilter}
                      label={t('teamManagement.filterByDepartment')}
                      onChange={(e) => setDepartmentFilter(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>{t('common.all')}</em>
                      </MenuItem>
                      {departments.map((dept) => (
                        <MenuItem key={dept.value || 'null'} value={dept.value || 'null'}>
                          {dept.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={12} md={4}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      onClick={handleClearFilters}
                      color="primary"
                      disabled={!departmentFilter && !nameSearchFilter}
                      title={t('common.clearFilters')}
                    >
                      <ClearIcon />
                    </IconButton>
                    <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center', ml: 1 }}>
                      {filteredSubordinates.length} / {subordinates.length} {t('teamManagement.usersShown')}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          )}

          <Divider sx={{ mb: 3 }} />

          {subordinates.length === 0 ? (
            <Typography variant="body1" color="text.secondary">
              {userRole === ROLES.ADMIN ? t('teamManagement.noEmployees') : t('teamManagement.noTeamMembers')}
            </Typography>
          ) : filteredSubordinates.length === 0 ? (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              {t('teamManagement.noMatchingUsers')}
            </Typography>
          ) : (
            <List>
              {filteredSubordinates.map((user) => (
                <ListItem key={user.id}>
                  <ListItemText
                    primary={user.name}
                    secondary={
                      <React.Fragment>
                        {t('teamManagement.role')}<Chip size="small" label={user.role} color="primary" />
                        {' | '}
                        {t('teamManagement.department')}
                        <Chip
                          size="small"
                          label={user.department || t('admin.noDepartment')}
                          color={user.department ? 'info' : 'default'}
                          variant={user.department ? 'filled' : 'outlined'}
                          sx={{ ml: 0.5 }}
                        />
                      </React.Fragment>
                    }
                  />
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      startIcon={<DescriptionIcon />}
                      onClick={() => handleCV(user)}
                      color="primary"
                      sx={{
                        textTransform: 'none'
                      }}
                    >
                      {t('teamManagement.cvAnalysis')}
                    </Button>
                    <Button
                      size="small"
                      startIcon={<WorkIcon />}
                      onClick={() => handleJobSkills(user)}
                      color="primary"
                      sx={{
                        textTransform: 'none'
                      }}
                    >
                      {t('teamManagement.jobSkills')}
                    </Button>
                    <Button
                      size="small"
                      startIcon={<SchoolIcon />}
                      onClick={() => handleTrainingRecommendations(user)}
                      color="primary"
                      sx={{
                        textTransform: 'none'
                      }}
                    >
                      {t('teamManagement.trainingRecommendations')}
                    </Button>
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      );
    } else if (viewMode === 'cv') {
      return (
        <Box>
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 2 }} />
              <Typography variant="h6">
                {t('teamManagement.cvAnalysisFor', { name: selectedUser.name })}
              </Typography>
            </Box>
            <Button onClick={handleBackToList}>
              {t('teamManagement.backToTeamList')}
            </Button>
          </Box>
          <Divider sx={{ mb: 3 }} />
          <CVAnalyzerPage userId={selectedUser.id} />
        </Box>
      );
    } else if (viewMode === 'job') {
      return (
        <Box>
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 2 }} />
              <Typography variant="h6">
                {t('teamManagement.jobSkillsFor', { name: selectedUser.name })}
              </Typography>
            </Box>
            <Button onClick={handleBackToList}>
              {t('teamManagement.backToTeamList')}
            </Button>
          </Box>
          <Divider sx={{ mb: 3 }} />
          <JobAnalyzerPage userId={selectedUser.id} />
        </Box>
      );
    } else if (viewMode === 'training') {
      return (
        <Box>
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 2 }} />
              <Typography variant="h6">
                {t('teamManagement.trainingRecommendationsFor', { name: selectedUser.name })}
              </Typography>
            </Box>
            <Button onClick={handleBackToList}>
              {t('teamManagement.backToTeamList')}
            </Button>
          </Box>
          <Divider sx={{ mb: 3 }} />
          <ResultPage userId={selectedUser.id} />
        </Box>
      );
    } else if (viewMode === 'reports') {
      return (
        <Box>
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AssessmentIcon sx={{ mr: 2 }} />
              <Typography variant="h6">
                {userRole === ROLES.ADMIN ? t('teamManagement.companySkillsGapReport') : t('teamManagement.teamSkillsGapReport')}
              </Typography>
            </Box>
            <Button onClick={handleBackToList}>
              {t('teamManagement.backToTeamList')}
            </Button>
          </Box>
          <Divider sx={{ mb: 3 }} />
          <ReportsView />
        </Box>
      );
    }
    return null;
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom align="center">
            {t('teamManagement.title')}
          </Typography>
          <Typography variant="body1" paragraph align="center">
            {t('teamManagement.description')}
          </Typography>

          {renderContent()}
        </Paper>
      </Box>
    </Container>
  );
}

export default TeamManagementPage;
